import { useState, useEffect, useCallback, useRef } from 'react';
import {
    fetchTodayDashboard,
    fetchForecastDashboard,
    fetchObservationDashboard,
    fetchEndOfStayDashboard,
} from '../../../../services/dashboardOperations';
import { handleApiError } from '../../../../utils/errorHandler';

const REFRESH_INTERVAL_MS = 60_000;

const useDashboardQuery = (fetchFn, filters, enabled = true) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const load = useCallback(async (showLoading = true) => {
        if (!enabled) return;

        if (showLoading) {
            setLoading(true);
        }
        setError(null);

        try {
            const response = await fetchFn(filtersRef.current);
            setData(response);
        } catch (requestError) {
            console.error('Dashboard load failed:', requestError);
            setError(handleApiError(requestError, 'Erreur de chargement du tableau de bord'));
        } finally {
            setLoading(false);
        }
    }, [fetchFn, enabled]);

    // Clé stable pour éviter les rechargements si l’objet filters change de référence à valeurs égales
    const filtersKey = JSON.stringify(filters ?? {});

    useEffect(() => {
        load(true);
    }, [load, filtersKey]);

    useEffect(() => {
        if (!enabled) return undefined;

        const intervalId = setInterval(() => load(false), REFRESH_INTERVAL_MS);
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') load(false);
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [load, enabled]);

    const refetch = useCallback(() => load(true), [load]);

    return { data, loading, error, refetch, isError: Boolean(error) };
};

export const useTodayDashboard = (filters, enabled = true) => (
    useDashboardQuery(fetchTodayDashboard, filters, enabled)
);

export const useForecastDashboard = (filters, enabled = true) => (
    useDashboardQuery(fetchForecastDashboard, filters, enabled)
);

export const useObservationDashboard = (filters, enabled = true) => (
    useDashboardQuery(fetchObservationDashboard, filters, enabled)
);

export const useEndOfStayDashboard = (filters, enabled = true) => (
    useDashboardQuery(fetchEndOfStayDashboard, filters, enabled)
);
